import { BaseApi, ApiResponse } from "../utils/request";

// 救援金任务信息
export interface RescueFundTaskInfo {
  id: string;
  name: string;
  times: "DAY" | "WEEK" | "MONTH" | "UNLIMITED" | "FIVE_MIN";
  rescueFundData: {
    type: "VIP_LEVEL" | "NET_LOSS";
    rescueRatio: number;
    maxRescueAmount?: number;
    minNetLossAmount?: number;
  };
  vipLevelConfigs?: Array<{
    vipLevel: number;
    rescueRatio: number;
    maxRescueAmount?: number;
    auditRatio: number;
    minBetAmount: number;
    minRechargeAmount: number;
  }>;
  userGroupConfigs?: Array<{
    userGroup: string;
    maxRescueAmount?: number;
    minBetAmount?: number;
    minRechargeAmount?: number;
    netLossConfigs: Array<{
      netLossAmount: number;
      rescueRatio: number;
      auditRatio: number;
    }>;
  }>;
  displayConfig?: {
    activityEntry?: string[];
    homepageMainBannerImageUrl?: string;
    homepageLeftBannerImageUrl?: string;
    homepageRightBannerImageUrl?: string;
    activityListBannerImageUrl?: string;
    floatingIconImageUrl?: string;
    ruleTitle?: string;
    ruleDescription?: string;
  };
  [key: string]: any;
}

// 用户救援金任务记录
export interface UserRescueFundTask {
  id: string;
  taskId: string;
  userId: string;
  date: string;
  rewardAmount: number;
  current: number;
  target: number;
  settleResult: "NEW" | "SUCCESS" | "FAILED";
  settleDate?: string;
  paid: boolean;
  paidTime?: string;
  data?: any;
  errMsg?: string;
  bonusTask?: RescueFundTaskInfo;
  [key: string]: any;
}

// 救援金任务列表响应
export interface RescueFundTaskListResponse {
  list: Array<{
    id: string;
    name: string;
    times: string;
    rescueFundData: {
      type: string;
      rescueRatio: number;
    };
    enabled: boolean;
  }>;
}

// 用户救援金任务信息响应
export interface UserRescueFundTaskInfoResponse {
  taskInfo?: RescueFundTaskInfo;
  userTask?: UserRescueFundTask;
  currentVipLevel?: number;
  currentNetLoss?: number;
  currentRescueRate?: number;
  nextLevelRescueRate?: number;
  expectedAmount?: number;
  nextPeriodStartTime?: string;
  nextPeriodEndTime?: string;
  /** 活动国家时区下的周期展示（当天 06:00 ~ 23:59:59），优先用此显示 */
  currentPeriodStartDisplay?: string;
  currentPeriodEndDisplay?: string;
  canClaim?: boolean;
  claimTime?: string;
  /** true=存在上一周期未领取的已结算（应展示 cashbackAmount）；false=本周期，展示 expectedCashbackAmount */
  hasUnclaimedSettledTask?: boolean;
  [key: string]: any;
}

export class RescueFundApi extends BaseApi {
  constructor() {
    super();
  }

  // 获取救援金任务列表
  async getTaskList(): Promise<ApiResponse<RescueFundTaskListResponse>> {
    return this.post<RescueFundTaskListResponse>("/api/rescue_fund_task/list", {
      query: { enabled: true },
    });
  }

  // 获取用户救援金任务信息
  async getUserTaskInfo(
    taskId: string
  ): Promise<ApiResponse<UserRescueFundTaskInfoResponse, true>> {
    return this.post<UserRescueFundTaskInfoResponse, true>(
      "/api/rescue_fund_task/user_info",
      { taskId }
    );
  }

  // 领取救援金
  // 注意：taskId 应该是 UserRescueFundTask 的 ID，不是 RescueFundTask 的 ID
  async claimRescueFund(taskId: string): Promise<ApiResponse<number, true>> {
    return this.post<number, true>("/api/rescue_fund_task/draw", {
      id: taskId,
    });
  }
}

export const rescueFundApi = new RescueFundApi();

export const GetRescueFundTaskList = () => rescueFundApi.getTaskList();

export const GetUserRescueFundTaskInfo = (params: { taskId: string }) =>
  rescueFundApi.getUserTaskInfo(params.taskId);

export const ClaimRescueFund = (params: { taskId: string }) =>
  rescueFundApi.claimRescueFund(params.taskId);
